import { useLayoutEffect, useRef } from "react";

interface FitOptions {
  bounds: { width: number; height: number };
  firstActive: { x: number; y: number } | undefined;
  tile: { width: number; height: number };
  scaleMin: number;
  scaleMax: number;
}

function wrapToTile(offset: number, tileSize: number): number {
  return tileSize > 0 ? ((offset % tileSize) + tileSize) % tileSize : 0;
}

export function useHoneycombFit({
  bounds,
  firstActive,
  tile,
  scaleMin,
  scaleMax,
}: FitOptions) {
  const fitterRef = useRef<HTMLDivElement | null>(null);
  const bgRef = useRef<HTMLDivElement | null>(null);
  const sizerRef = useRef<HTMLDivElement | null>(null);
  const scalerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const fitter = fitterRef.current;
    const bg = bgRef.current;
    const sizer = sizerRef.current;
    const scaler = scalerRef.current;
    if (!fitter || !bg || !sizer || !scaler) return;

    const apply = () => {
      const { width: fitterWidth, height: fitterHeight } =
        fitter.getBoundingClientRect();
      if (fitterWidth === 0 || fitterHeight === 0) return;

      const widthScale = fitterWidth / bounds.width;
      const heightScale = fitterHeight / bounds.height;
      const scale = Math.min(
        scaleMax,
        Math.max(scaleMin, Math.min(widthScale, heightScale)),
      );

      fitter.style.overflowX = widthScale < scale ? "auto" : "hidden";
      fitter.style.overflowY = heightScale < scale ? "auto" : "hidden";
      sizer.style.width = `${bounds.width * scale}px`;
      sizer.style.height = `${bounds.height * scale}px`;
      scaler.style.transform = `scale(${scale})`;

      const anchor = firstActive ?? { x: 0, y: 0 };
      const sizerRect = sizer.getBoundingClientRect();
      const bgRect = bg.getBoundingClientRect();
      const scaledTileWidth = tile.width * scale;
      const scaledTileHeight = tile.height * scale;
      const bgPositionX = wrapToTile(
        sizerRect.left - bgRect.left + anchor.x * scale,
        scaledTileWidth,
      );
      const bgPositionY = wrapToTile(
        sizerRect.top - bgRect.top + anchor.y * scale,
        scaledTileHeight,
      );

      bg.style.backgroundSize = `${scaledTileWidth}px ${scaledTileHeight}px`;
      bg.style.backgroundPosition = `${bgPositionX}px ${bgPositionY}px`;
    };

    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(fitter, { box: "border-box" });
    return () => ro.disconnect();
  }, [bounds, firstActive, tile, scaleMin, scaleMax]);

  return { fitterRef, bgRef, sizerRef, scalerRef };
}
